import { Suspense } from "react";
import { Office } from "./Office";
import { HotelSuite } from "./HotelSuite";
import { SocialOffice } from "./SocialOffice";
import { Restaurant } from "./Restaurant";
import { HackerRoom } from "./HackerRoom";
import { LivingRoom } from "./LivingRoom";
import { Apartment } from "./Apartment";
import { TrainCarriage } from "./TrainCarriage";
import { TrainPlatform } from "./TrainPlatform";
import { WalkableOffice } from "./WalkableOffice";
import { SplatWorld } from "./SplatWorld";
import { PanoWorld } from "./PanoWorld";
import { GltfRoom } from "./GltfRoom";
import { OutdoorSky } from "./OutdoorSky";
import { WaterSurface } from "./WaterSurface";
import { BeachSurround } from "./BeachSurround";
import { DeskMobile } from "./DeskMobile";
import type { DeviceTier, EnvironmentSpec } from "../scenario/types";

const SPLAT_READY = import.meta.env.VITE_SPLAT_READY === "true";

function siblingPano(spzUrl: string): string {
  // Strip ?v= / #hash so /world_2m.spz?v=x still maps to sibling pano.jpg
  const bare = spzUrl.split(/[?#]/)[0];
  const swapped = bare.replace(/\/world_[^/]+\.spz$/i, "/pano.jpg");
  return swapped === bare ? bare.replace(/\/[^/]+\.spz$/i, "/pano.jpg") : swapped;
}

export function Environment3D({
  env,
  tier,
}: {
  env: EnvironmentSpec;
  tier: DeviceTier;
}) {
  const Procedural =
    env.procedural === "hotel_suite"
      ? HotelSuite
      : env.procedural === "office_social"
      ? SocialOffice
      : env.procedural === "restaurant"
      ? Restaurant
      : env.procedural === "hacker_room"
      ? HackerRoom
      : env.procedural === "living_room"
      ? LivingRoom
      : env.procedural === "apartment"
      ? Apartment
      : env.procedural === "train_carriage"
      ? TrainCarriage
      : env.procedural === "train_platform"
      ? TrainPlatform
      : env.procedural === "walkable_office"
      ? WalkableOffice
      : Office;

  const splatRef =
    tier === "high"
      ? env.splat.high ?? env.splat.mid
      : tier === "mid"
      ? env.splat.mid ?? env.splat.high
      : env.splat.mid ?? env.splat.high;

  // Prefer explicit low/pano.jpg — never feed a .spz URL into TextureLoader
  // (siblingPano used to break when high/mid URLs carried ?v= cache busters).
  const panoUrl =
    env.panoUrl ||
    (env.splat.low?.url && !/\.spz(\?|$)/i.test(env.splat.low.url)
      ? env.splat.low.url
      : undefined) ||
    (splatRef?.url ? siblingPano(splatRef.url) : undefined);

  // Photoreal pano (locked look-around) wins over unproven .spz alignment.
  if (env.enablePano && panoUrl) {
    return (
      <PanoWorld
        url={panoUrl}
        rotationYDeg={env.panoRotationYDeg ?? 0}
        flipVertical={!!env.panoFlipVertical}
      />
    );
  }

  if (env.gltfUrl) {
    const outdoor = env.outdoor;
    return (
      <>
        {outdoor ? <OutdoorSky spec={outdoor} /> : null}
        <Suspense fallback={null}>
          <GltfRoom
            url={env.gltfUrl}
            scale={env.gltfScale ?? 1}
            position={env.gltfPosition ?? [0, 0, 0]}
            rotationDeg={env.gltfRotationDeg ?? [0, 0, 0]}
            shadows={!!outdoor?.shadows}
          />
        </Suspense>
        {env.beachSurround ? <BeachSurround ipadScreen={env.beachIpad ?? "teams"} notice={env.beachIpadNotice} drink={env.beachDrink} /> : null}
        {env.deskMobile ? <DeskMobile spec={env.deskMobile} /> : null}
        {env.water ? <WaterSurface spec={env.water} /> : null}
        {(env.waters ?? []).map((w, i) => (
          <WaterSurface key={`water-${i}`} spec={w} />
        ))}
      </>
    );
  }

  if (SPLAT_READY && env.enableSplat && splatRef?.url) {
    const t = env.splatTransform;
    return (
      <Suspense fallback={null}>
        <SplatWorld
          url={splatRef.url}
          panoUrl={panoUrl}
          rotationDeg={t?.rotationDeg}
          scale={t?.scale ?? 1}
          position={t?.position ?? [0, 0, 0]}
          autoFit
          targetSpanM={8}
          fallback={null}
        />
      </Suspense>
    );
  }

  // Restaurant's sign board is branded per-scenario (env.props.signText/signSub)
  // so this procedural room can be shared across courses without one course's
  // branding leaking into another's. Other procedural rooms take no props.
  return (
    <Suspense fallback={null}>
      {env.procedural === "restaurant" ? (
        <Restaurant signText={env.props?.signText as string | undefined} signSub={env.props?.signSub as string | undefined} />
      ) : (
        <Procedural />
      )}
    </Suspense>
  );
}
